// User-side page for the conversation with the AddVendTour admin team.
// Reachable from /inbox (unified list) or the bell (admin_message deep-link
// lands here). Renders every message oldest-first with a reply composer at
// the bottom. When the user opens the page we auto-mark admin->user
// messages as read (backend does this server-side on the GET).
import { useEffect, useRef, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function timeStamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function InboxAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/inbox/admin/thread");
      setMessages(data.messages || []);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    // Scroll the newest message into view whenever the list grows.
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/inbox/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await api.post("/inbox/admin/reply", { body: text });
      setMessages((prev) => [...prev, data.message]);
      setBody("");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send reply"
      );
    } finally {
      setSending(false);
    }
  };

  const hasThread = messages.length > 0;

  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12"
      data-testid="inbox-admin-page"
    >
      <Link
        to="/inbox"
        data-testid="inbox-admin-back"
        className="inline-flex items-center gap-1.5 text-sm mb-4"
        style={{ color: "var(--ink-soft)" }}
      >
        <ArrowLeft size={14} /> All messages
      </Link>

      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            backgroundColor: "rgba(107,47,186,0.10)",
            color: "var(--avt-purple)",
          }}
        >
          <ShieldCheck size={22} />
        </div>
        <div>
          <div
            className="text-[10px] uppercase tracking-widest"
            style={{ color: "var(--terracotta)" }}
          >
            Direct message
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            AddVendTour team
          </h1>
        </div>
      </div>

      <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
        Anything you send here goes straight to the AddVendTour admin — help,
        feedback, questions, whatever's on your mind.
      </p>

      {error && (
        <div
          data-testid="inbox-admin-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{
            backgroundColor: "rgba(31,93,52,0.08)",
            color: "var(--terracotta)",
          }}
        >
          {error}
        </div>
      )}

      <div
        data-testid="inbox-admin-thread"
        className="mt-6 rounded-lg border p-3 sm:p-4 space-y-3"
        style={{
          borderColor: "rgba(0,0,0,0.08)",
          backgroundColor: "#fff",
          minHeight: 240,
        }}
      >
        {loading ? (
          <div
            className="py-16 flex items-center justify-center text-sm"
            style={{ color: "var(--ink-soft)" }}
            data-testid="inbox-admin-loading"
          >
            <Loader2 size={14} className="animate-spin mr-2" />
            Loading conversation…
          </div>
        ) : !hasThread ? (
          <div
            className="py-16 text-center text-sm space-y-2"
            style={{ color: "var(--ink-soft)" }}
            data-testid="inbox-admin-empty"
          >
            <ShieldCheck size={22} className="inline" style={{ color: "var(--avt-purple)" }} />
            <p className="max-w-md mx-auto">
              Say hi — this is a direct line to the AddVendTour team. Send us a
              question, feedback, or just an intro and we'll reply here.
            </p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role === "user";
            return (
              <div
                key={m.id}
                data-testid={`inbox-admin-message-${m.id}`}
                data-sender={m.sender_role}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className="rounded-lg px-3 py-2 max-w-[85%]"
                  style={{
                    backgroundColor: mine
                      ? "rgba(107,47,186,0.10)"
                      : "rgba(201,162,75,0.16)",
                    borderTopLeftRadius: mine ? undefined : 4,
                    borderTopRightRadius: mine ? 4 : undefined,
                  }}
                >
                  {m.subject && m.sender_role === "admin" && (
                    <div
                      className="text-[10px] uppercase tracking-widest mb-1"
                      style={{ color: "var(--terracotta)" }}
                    >
                      {m.subject}
                    </div>
                  )}
                  <div
                    className="text-sm whitespace-pre-wrap"
                    style={{ color: "var(--ink)" }}
                  >
                    {m.body}
                  </div>
                  <div
                    className="text-[10px] mt-1.5 text-right"
                    style={{ color: "var(--ink-soft)" }}
                  >
                    {mine ? "You" : "AddVendTour"} · {timeStamp(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={submit} className="mt-4 space-y-2">
        <Textarea
          data-testid="inbox-admin-reply-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            hasThread
              ? "Type your reply…"
              : "Hi — I'd love to ask about…"
          }
          rows={4}
          maxLength={5000}
          disabled={sending}
        />
        <div className="flex items-center justify-between gap-3">
          <div
            className="text-[11px]"
            style={{ color: "var(--ink-soft)" }}
          >
            {body.length} / 5000
          </div>
          <Button
            type="submit"
            data-testid="inbox-admin-reply-send"
            disabled={sending || !body.trim()}
            style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
          >
            {sending ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Sending…
              </>
            ) : (
              <>
                <Send size={14} className="mr-2" />
                {hasThread ? "Send reply" : "Send message"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
